/**
 * evaluateRouteGuard — pure-function implementation of the per-route guard.
 *
 * Extracted from router/index.js's `router.beforeEach` so the three rules can
 * be unit-tested without spinning up a router, mounting components, or
 * mocking the saas-core authStore. Called from beforeEach with the resolved
 * auth state.
 *
 * Returns:
 *   - a route location object → vue-router redirects there
 *   - undefined → navigation proceeds to `to`
 *
 * Rules (in order):
 *   0. bare "/" + logged in               → /home (the root IS the public Topics
 *      landing — an aliased route, not a redirect — so an anonymous visitor stays
 *      on the bare URL. A signed-in user gets the dashboard instead.)
 *   1. requiresAuth + not logged in       → /login (preserving the target as ?redirect=)
 *   2. requiresAdmin + not platform admin → /home (the consumer home dashboard)
 *   3. guestOnly + already logged in      → /home (admins included; the admin
 *      console is reachable only by URL or via the sidebar "Admin console" link
 *      that renders for admins inside the consumer surface)
 */
export function evaluateRouteGuard(to, auth) {
  // "/" is the PUBLIC STOREFRONT (PublicLayout + PublicHome), rendered not
  // redirected, so an anonymous visitor lands on app.i-recall.au with no bounce.
  // A signed-in user is sent on to their dashboard instead.
  if (to.path === '/' && auth.isLoggedIn) {
    return { path: '/home' };
  }
  // /topics is the SIGNED-IN deck browser (drill-down picker + History/Stats).
  // An anonymous visitor belongs on the storefront, which is the same catalogue
  // dressed for a first-time visitor. The player and Map stay open to them.
  if (to.meta?.signedInBrowse && !auth.isLoggedIn) {
    return { path: '/' };
  }
  if (to.meta?.requiresAuth && !auth.isLoggedIn) {
    // "Soft-private" routes (the home dashboard) send an anonymous visitor to
    // the PUBLIC Topics demo rather than a login wall — the try-before-signup
    // landing. It lands on the BARE ROOT (not /topics) so the marketing URL is
    // clean. Genuinely private routes still bounce to /login (target preserved).
    if (to.meta?.publicFallback) return { path: '/' };
    return { path: '/login', query: { redirect: to.fullPath } };
  }
  if (to.meta?.requiresAdmin && !auth.isAdmin) {
    return { path: '/home' };
  }
  if (to.meta?.guestOnly && auth.isLoggedIn) {
    return { path: '/home' };
  }
  return undefined;
}
